import React, { FormEvent, FunctionComponent, useCallback } from 'react';
import styled from 'styled-components';

import {
  CardList,
  SaveEmailSubmitButtonGroup,
  TickedDiscountItem,
} from 'components';
import { Heading } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';

import { getDecodedStrippedString } from 'scenes/common/helpers/html';
import {
  DEFAULT_ONLINE_CAMPAIGN,
  getInstalmentFootnote,
} from 'scenes/common/helpers/insuranceInfo';
import { toPercentage } from 'scenes/common/helpers/percentage';

import { LandlordFullPageProps } from 'scenes/landlord/types';
import {
  getCampaign,
  getPreselectedPaymentFrequency,
  getPreselectedPrice,
  getSelectedCoverType,
  getSelectedInsuranceOption,
  isMonthlyPaymentPreselected,
} from 'scenes/landlord/selectors';
import { PriceCard } from 'scenes/common/components/QuoteInformation/QuotePriceCards/PriceCard';

import { SCREEN } from 'scenes/common/types/screen';
import { DEFAULT_HEADING_SIZES } from 'scenes/common/helpers/fonts';
import { AriaLive } from 'scenes/common/types';
import { QuotePriceExcess } from './QuotePriceExcess';

export const QUESTION = 'Your Coles Landlord Insurance Quote';

const FootnoteWrapper = styled.div`
  margin-top: 1rem;
  margin-bottom: 1.5rem;
`;

const QuotePriceStepWrapper = styled.div`
  label {
    margin-bottom: 0.5em;

    @media (min-width: ${SCREEN.SMALL}px) {
      font-size: 2em !important;
    }
  }

  label + p {
    margin-top: 0.5em !important;
  }

  .c-btn--tertiary:first-of-type {
    padding: 0 1em;
    margin-left: -0.5em !important;
  }
`;

export type QuotePriceStepProps = Pick<
  LandlordFullPageProps,
  | 'funnelState'
  | 'onNext'
  | 'onClickSaveQuoteModal'
  | 'onSwitchPaymentFrequency'
>;

export const QuotePriceStep: FunctionComponent<QuotePriceStepProps> = ({
  funnelState,
  onNext,
  onClickSaveQuoteModal,
  onSwitchPaymentFrequency,
}) => {
  const onSubmit = useCallback(
    (event: FormEvent) => {
      onNext({});
      event.preventDefault();
    },
    [onNext]
  );

  const campaign = getCampaign(funnelState);
  const {
    premium: {
      annualPrice,
      price,
      yearlyGovernmentCharges,
      monthlyGovernmentCharges,
    },
  } = getSelectedInsuranceOption(funnelState);
  const savedAmount = Number(price) - Number(annualPrice);

  return (
    <form onSubmit={onSubmit}>
      <QuotePriceStepWrapper>
        <Heading size={1} margin={{ top: 2 }} fontSize={DEFAULT_HEADING_SIZES}>
          {QUESTION}
        </Heading>
        <CardList>
          <PriceCard
            frequency={getPreselectedPaymentFrequency(funnelState)}
            coverType={getSelectedCoverType(funnelState)}
            amount={getPreselectedPrice(funnelState)}
            onSwitchPaymentFrequency={onSwitchPaymentFrequency}
            onClick={() => {}}
            selected
            showGovernmentCharges
            yearlyGovernmentCharges={yearlyGovernmentCharges}
            monthlyGovernmentCharges={monthlyGovernmentCharges}
            footnoteText={getInstalmentFootnote(
              isMonthlyPaymentPreselected(funnelState),
              price,
              savedAmount
            )}
            ariaLive={AriaLive.assertive}
          />
        </CardList>
        <FootnoteWrapper>
          <TickedDiscountItem
            campaignDescription={`${toPercentage(
              DEFAULT_ONLINE_CAMPAIGN.discount
            )} online discount applied`}
          />
          {campaign && (
            <TickedDiscountItem
              campaignDescription={getDecodedStrippedString(
                campaign.elements[0].description
              )}
            />
          )}
          <QuotePriceExcess funnelState={funnelState} />
          <TextSmall margin={{ bottom: 0 }}>
            * Includes GST, government charges and any discount.
          </TextSmall>
        </FootnoteWrapper>
        <SaveEmailSubmitButtonGroup
          onClickSaveQuoteModal={onClickSaveQuoteModal}
          submitEnabled
        />
      </QuotePriceStepWrapper>
    </form>
  );
};

export default QuotePriceStep;
